import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
    TrendingUp, TrendingDown, DollarSign, ShoppingCart, 
    Users, Clock, AlertTriangle, CheckCircle, XCircle,
    RefreshCw, ChefHat, Wine, Package, BarChart3,
    Calendar, Filter, ArrowUp, ArrowDown, Minus,
    Sun, Moon
} from 'lucide-react';
import { 
    AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, 
    ResponsiveContainer, PieChart, Pie, Cell, Legend,
    BarChart, Bar
} from 'recharts';
import { toast } from 'sonner';
import OpenRegistersIndicator from '../components/OpenRegistersIndicator';

// ======================== KPI CARD COMPONENT ========================
const KPICard = ({ title, value, formatted, variation, icon: Icon, isDark }) => {
    const getVariationColor = (status) => {
        if (status === 'good') return isDark ? 'text-emerald-400' : 'text-emerald-600';
        if (status === 'neutral') return isDark ? 'text-amber-400' : 'text-amber-600';
        return isDark ? 'text-red-400' : 'text-red-600';
    };

    const getVariationBg = (status) => {
        if (status === 'good') return isDark ? 'bg-emerald-500/20' : 'bg-emerald-100';
        if (status === 'neutral') return isDark ? 'bg-amber-500/20' : 'bg-amber-100';
        return isDark ? 'bg-red-500/20' : 'bg-red-100';
    };

    const VariationIcon = variation?.value > 0 ? ArrowUp : (variation?.value < 0 ? ArrowDown : Minus);

    return (
        <div className={`p-4 lg:p-6 rounded-2xl transition-all duration-300 ${
            isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg shadow-gray-200/50'
        }`}>
            <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 rounded-xl ${
                    isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                }`}>
                    <Icon className={`w-5 h-5 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                {variation && (
                    <div className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold ${
                        getVariationBg(variation.status)
                    } ${getVariationColor(variation.status)}`}>
                        <VariationIcon className="w-3 h-3" />
                        {Math.abs(variation.value)}%
                    </div>
                )}
            </div>
            <h3 className={`text-sm font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                {title}
            </h3>
            <p className={`text-2xl lg:text-3xl font-bold tracking-tight ${
                isDark ? 'text-white' : 'text-gray-900'
            }`}>
                {formatted || value}
            </p>
        </div>
    );
};

// ======================== ALERT CARD COMPONENT ========================
const AlertCard = ({ alert, isDark }) => {
    const typeStyles = {
        success: isDark ? 'bg-emerald-500/20 border-emerald-500/30' : 'bg-emerald-50 border-emerald-200',
        warning: isDark ? 'bg-amber-500/20 border-amber-500/30' : 'bg-amber-50 border-amber-200',
        danger: isDark ? 'bg-red-500/20 border-red-500/30' : 'bg-red-50 border-red-200'
    };

    const iconColors = {
        success: isDark ? 'text-emerald-400' : 'text-emerald-600',
        warning: isDark ? 'text-amber-400' : 'text-amber-600',
        danger: isDark ? 'text-red-400' : 'text-red-600'
    };

    const AlertIcon = alert.type === 'success' ? CheckCircle : 
                      alert.type === 'danger' ? XCircle : AlertTriangle;

    return (
        <div className={`p-4 rounded-xl border ${typeStyles[alert.type]} transition-all`}>
            <div className="flex items-start gap-3">
                <AlertIcon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${iconColors[alert.type]}`} />
                <div>
                    <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {alert.title}
                    </h4>
                    <p className={`text-sm mt-0.5 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                        {alert.message}
                    </p>
                </div>
            </div>
        </div>
    );
};

// ======================== MAIN PERFORMANCE PAGE ========================
const PerformancePage = () => {
    const { api, user } = useAuth();
    const [isDark, setIsDark] = useState(true);
    const [period, setPeriod] = useState('today');
    const [zone, setZone] = useState('all');
    const [loading, setLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(new Date());
    
    // Data states
    const [stats, setStats] = useState(null);
    const [hourlySales, setHourlySales] = useState([]);
    const [breakdown, setBreakdown] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [operations, setOperations] = useState(null);
    const [byServer, setByServer] = useState([]);
    const [tableStats, setTableStats] = useState(null);
    const [alerts, setAlerts] = useState([]);

    const refreshIntervalRef = useRef(null);

    // Colors for charts
    const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    const AREA_COLORS = {
        salle: '#3B82F6',
        bar: '#10B981',
        emporter: '#F59E0B'
    };

    // ======================== DATA FETCHING ========================
    const fetchAllData = useCallback(async () => {
        try {
            const [
                statsRes,
                hourlyRes,
                breakdownRes,
                topProductsRes,
                operationsRes,
                byServerRes,
                tableStatsRes,
                alertsRes
            ] = await Promise.all([
                api().get(`/performance/stats?period=${period}&zone=${zone}`),
                api().get(`/performance/hourly-sales?period=${period}&zone=${zone}`),
                api().get(`/performance/breakdown?period=${period}&zone=${zone}`),
                api().get(`/performance/top-products?period=${period}&zone=${zone}`),
                api().get(`/performance/operations?period=${period}`),
                api().get(`/performance/by-server?period=${period}`),
                api().get(`/performance/tables?period=${period}`),
                api().get(`/performance/alerts`)
            ]);

            setStats(statsRes.data);
            setHourlySales(hourlyRes.data.data || []);
            setBreakdown(breakdownRes.data);
            setTopProducts(topProductsRes.data.products || []);
            setOperations(operationsRes.data);
            setByServer(byServerRes.data.servers || []);
            setTableStats(tableStatsRes.data);
            setAlerts(alertsRes.data.alerts || []);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching performance data:', error);
            toast.error('Erreur lors du chargement des données');
            setLoading(false);
        }
    }, [api, period, zone]);

    // Initial load and refresh setup
    useEffect(() => {
        fetchAllData();
        
        // Auto-refresh every 30 seconds
        refreshIntervalRef.current = setInterval(fetchAllData, 30000);
        
        return () => {
            if (refreshIntervalRef.current) {
                clearInterval(refreshIntervalRef.current);
            }
        };
    }, [fetchAllData]);

    // Clock update
    useEffect(() => {
        const clockInterval = setInterval(() => {
            setCurrentTime(new Date());
        }, 1000);
        return () => clearInterval(clockInterval);
    }, []);

    // ======================== RENDER ========================
    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${
                isDark ? 'bg-gray-900' : 'bg-gray-50'
            }`}>
                <div className="flex flex-col items-center gap-4">
                    <RefreshCw className={`w-12 h-12 animate-spin ${
                        isDark ? 'text-blue-400' : 'text-blue-600'
                    }`} />
                    <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>
                        Chargement des données...
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen transition-colors duration-300 ${
            isDark ? 'bg-gray-900' : 'bg-gray-50'
        }`}>
            {/* ======================== HEADER ======================== */}
            <header className={`sticky top-0 z-50 px-4 lg:px-6 py-4 border-b backdrop-blur-xl ${
                isDark ? 'bg-gray-900/80 border-gray-800' : 'bg-white/80 border-gray-200'
            }`}>
                <div className="max-w-7xl mx-auto">
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                        {/* Title and time */}
                        <div className="flex items-center gap-4">
                            <div className={`p-3 rounded-xl ${
                                isDark ? 'bg-blue-500/20' : 'bg-blue-100'
                            }`}>
                                <BarChart3 className={`w-6 h-6 ${
                                    isDark ? 'text-blue-400' : 'text-blue-600'
                                }`} />
                            </div>
                            <div>
                                <h1 className={`text-xl lg:text-2xl font-bold ${
                                    isDark ? 'text-white' : 'text-gray-900'
                                }`}>
                                    Performance
                                </h1>
                                <p className={`text-sm font-mono ${
                                    isDark ? 'text-gray-400' : 'text-gray-500'
                                }`}>
                                    {currentTime.toLocaleTimeString('fr-FR')}
                                </p>
                            </div>
                            
                            {/* Open Registers Indicator */}
                            <OpenRegistersIndicator showDetails={false} />
                        </div>

                        {/* Filters */}
                        <div className="flex flex-wrap items-center gap-3">
                            {/* Period selector */}
                            <div className="flex rounded-xl overflow-hidden border ${
                                isDark ? 'border-gray-700' : 'border-gray-200'
                            }">
                                {[
                                    { value: 'today', label: "Aujourd'hui" },
                                    { value: 'yesterday', label: 'Hier' },
                                    { value: 'week', label: 'Semaine' },
                                    { value: 'month', label: 'Mois' }
                                ].map((p) => (
                                    <button
                                        key={p.value}
                                        onClick={() => setPeriod(p.value)}
                                        className={`px-3 py-2 text-sm font-medium transition-all ${
                                            period === p.value
                                                ? isDark 
                                                    ? 'bg-blue-600 text-white' 
                                                    : 'bg-blue-600 text-white'
                                                : isDark 
                                                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700' 
                                                    : 'bg-white text-gray-600 hover:bg-gray-100'
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>

                            {/* Zone filter */}
                            <select
                                value={zone}
                                onChange={(e) => setZone(e.target.value)}
                                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                                    isDark 
                                        ? 'bg-gray-800 border-gray-700 text-gray-300' 
                                        : 'bg-white border-gray-200 text-gray-700'
                                }`}
                            >
                                <option value="all">Toutes zones</option>
                                <option value="salle">Salle</option>
                                <option value="terrasse">Terrasse</option>
                                <option value="bar">Bar</option>
                                <option value="emporter">À emporter</option>
                            </select>

                            {/* Refresh button */}
                            <button
                                onClick={fetchAllData}
                                className={`p-2.5 rounded-xl transition-all ${
                                    isDark 
                                        ? 'bg-gray-800 hover:bg-gray-700 text-gray-300' 
                                        : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-200'
                                }`}
                            >
                                <RefreshCw className="w-5 h-5" />
                            </button>

                            {/* Theme toggle */}
                            <button
                                onClick={() => setIsDark(!isDark)}
                                className={`p-2.5 rounded-xl transition-all ${
                                    isDark 
                                        ? 'bg-gray-800 hover:bg-gray-700 text-yellow-400' 
                                        : 'bg-white hover:bg-gray-100 text-gray-600 border border-gray-200'
                                }`}
                            >
                                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            {/* ======================== MAIN CONTENT ======================== */}
            <main className="max-w-7xl mx-auto px-4 lg:px-6 py-6 space-y-6">
                
                {/* ======================== KPI CARDS ======================== */}
                <section className="grid grid-cols-2 lg:grid-cols-5 gap-4">
                    <KPICard 
                        title="Chiffre d'affaires"
                        value={stats?.revenue?.value}
                        formatted={stats?.revenue?.formatted}
                        variation={stats?.revenue?.variation}
                        icon={DollarSign}
                        isDark={isDark}
                    />
                    <KPICard 
                        title="Commandes"
                        value={stats?.orders_count?.value}
                        variation={stats?.orders_count?.variation}
                        icon={ShoppingCart}
                        isDark={isDark}
                    />
                    <KPICard 
                        title="Panier moyen"
                        value={stats?.average_basket?.value}
                        formatted={stats?.average_basket?.formatted}
                        variation={stats?.average_basket?.variation}
                        icon={TrendingUp}
                        isDark={isDark}
                    />
                    <KPICard 
                        title="Tables servies"
                        value={stats?.tables_served?.value}
                        variation={stats?.tables_served?.variation}
                        icon={Users}
                        isDark={isDark}
                    />
                    <KPICard 
                        title="Temps moyen prépa"
                        value={stats?.avg_prep_time?.value}
                        formatted={stats?.avg_prep_time?.formatted}
                        variation={{ value: 0, status: stats?.avg_prep_time?.status || 'neutral' }}
                        icon={Clock}
                        isDark={isDark}
                    />
                </section>

                {/* ======================== CHARTS ROW ======================== */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* Hourly Sales Chart */}
                    <div className={`lg:col-span-2 p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 ${
                            isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                            Ventes par heure
                        </h2>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={hourlySales}>
                                    <defs>
                                        <linearGradient id="colorSalle" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={AREA_COLORS.salle} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={AREA_COLORS.salle} stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorBar" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={AREA_COLORS.bar} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={AREA_COLORS.bar} stopOpacity={0}/>
                                        </linearGradient>
                                        <linearGradient id="colorEmporter" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="5%" stopColor={AREA_COLORS.emporter} stopOpacity={0.3}/>
                                            <stop offset="95%" stopColor={AREA_COLORS.emporter} stopOpacity={0}/>
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#374151' : '#E5E7EB'} />
                                    <XAxis 
                                        dataKey="label" 
                                        stroke={isDark ? '#9CA3AF' : '#6B7280'}
                                        tick={{ fontSize: 12 }}
                                    />
                                    <YAxis 
                                        stroke={isDark ? '#9CA3AF' : '#6B7280'}
                                        tick={{ fontSize: 12 }}
                                        tickFormatter={(value) => `${(value/1000).toFixed(0)}k`}
                                    />
                                    <Tooltip 
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                                            border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
                                            borderRadius: '12px',
                                            color: isDark ? '#F9FAFB' : '#111827'
                                        }}
                                        formatter={(value) => [`${value.toLocaleString()} F`, '']}
                                    />
                                    <Legend />
                                    <Area 
                                        type="monotone" 
                                        dataKey="salle" 
                                        name="Salle"
                                        stroke={AREA_COLORS.salle} 
                                        fillOpacity={1} 
                                        fill="url(#colorSalle)" 
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="bar" 
                                        name="Bar"
                                        stroke={AREA_COLORS.bar} 
                                        fillOpacity={1} 
                                        fill="url(#colorBar)" 
                                    />
                                    <Area 
                                        type="monotone" 
                                        dataKey="emporter" 
                                        name="À emporter"
                                        stroke={AREA_COLORS.emporter} 
                                        fillOpacity={1} 
                                        fill="url(#colorEmporter)" 
                                    />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>

                    {/* Alerts */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                            isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                            <AlertTriangle className="w-5 h-5 text-amber-500" />
                            Alertes
                        </h2>
                        <div className="space-y-3 max-h-64 overflow-y-auto">
                            {alerts.map((alert, idx) => (
                                <AlertCard key={idx} alert={alert} isDark={isDark} />
                            ))}
                        </div>
                    </div>
                </div>

                {/* ======================== BREAKDOWN & TOP PRODUCTS ======================== */}
                <div className="grid lg:grid-cols-3 gap-6">
                    {/* By Type */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Par type
                        </h2>
                        <div className="flex justify-center items-center" style={{ height: '180px' }}>
                            {(breakdown?.by_type || []).filter(d => d.value > 0).length > 0 ? (
                                <PieChart width={180} height={180}>
                                    <Pie
                                        data={(breakdown?.by_type || []).filter(d => d.value > 0)}
                                        cx={90}
                                        cy={90}
                                        innerRadius={35}
                                        outerRadius={70}
                                        startAngle={90}
                                        endAngle={-270}
                                        paddingAngle={1}
                                        dataKey="value"
                                        nameKey="name"
                                        isAnimationActive={false}
                                    >
                                        {(breakdown?.by_type || []).filter(d => d.value > 0).map((entry, index) => (
                                            <Cell key={`cell-type-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        formatter={(value) => [`${value.toLocaleString()} F`, '']}
                                        contentStyle={{
                                            backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
                                            border: `1px solid ${isDark ? '#374151' : '#E5E7EB'}`,
                                            borderRadius: '8px'
                                        }}
                                    />
                                </PieChart>
                            ) : (
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Aucune donnée</p>
                            )}
                        </div>
                        <div className="space-y-2 mt-4">
                            {(breakdown?.by_type || []).filter(d => d.value > 0).map((item, idx) => (
                                <div key={idx} className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <div 
                                            className="w-3 h-3 rounded-full" 
                                            style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                                        />
                                        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {item.name}
                                        </span>
                                    </div>
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {item.percentage}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* ======================== CAMEMBERT PAR CATÉGORIE (AMÉLIORÉ) ======================== */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Répartition CA par catégorie
                        </h2>
                        
                        {/* Pie Chart with center label */}
                        <div className="relative flex justify-center items-center" style={{ height: '200px' }}>
                            {(() => {
                                const categoryData = (breakdown?.by_category || []).filter(c => c.value > 0).slice(0, 4);
                                const pieColors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
                                
                                if (categoryData.length === 0) {
                                    return <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Aucune donnée</p>;
                                }
                                
                                return (
                                    <PieChart width={200} height={200}>
                                        <Pie
                                            data={categoryData}
                                            cx={100}
                                            cy={100}
                                            innerRadius={45}
                                            outerRadius={80}
                                            startAngle={90}
                                            endAngle={-270}
                                            paddingAngle={categoryData.length > 1 ? 2 : 0}
                                            dataKey="value"
                                            nameKey="name"
                                            isAnimationActive={false}
                                            label={({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
                                                if (percent < 0.08) return null;
                                                const RADIAN = Math.PI / 180;
                                                const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
                                                const x = cx + radius * Math.cos(-midAngle * RADIAN);
                                                const y = cy + radius * Math.sin(-midAngle * RADIAN);
                                                return (
                                                    <text
                                                        x={x}
                                                        y={y}
                                                        fill="white"
                                                        textAnchor="middle"
                                                        dominantBaseline="central"
                                                        style={{ 
                                                            fontSize: '12px', 
                                                            fontWeight: 'bold',
                                                            textShadow: '0 1px 3px rgba(0,0,0,0.6)' 
                                                        }}
                                                    >
                                                        {`${(percent * 100).toFixed(0)}%`}
                                                    </text>
                                                );
                                            }}
                                            labelLine={false}
                                        >
                                            {categoryData.map((entry, index) => (
                                                <Cell key={`cell-cat-${index}`} fill={pieColors[index % pieColors.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip 
                                            content={({ active, payload }) => {
                                                if (active && payload && payload.length) {
                                                    const data = payload[0].payload;
                                                    return (
                                                        <div className={`px-4 py-3 rounded-xl shadow-xl border ${
                                                            isDark 
                                                                ? 'bg-gray-800 border-gray-700' 
                                                                : 'bg-white border-gray-200'
                                                        }`}>
                                                            <p className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                                {data.name}
                                                            </p>
                                                            <p className={`text-lg font-bold mt-1 ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                                                {data.formatted}
                                                            </p>
                                                            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                                {data.percentage}% du CA total
                                                            </p>
                                                        </div>
                                                    );
                                                }
                                                return null;
                                            }}
                                        />
                                    </PieChart>
                                );
                            })()}
                            
                            {/* Center total label - positioned absolutely */}
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                <div className="text-center">
                                    <p className={`text-[10px] font-medium uppercase tracking-wide ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        Total
                                    </p>
                                    <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        {breakdown?.total_revenue 
                                            ? `${breakdown.total_revenue.toLocaleString('fr-FR')} F`
                                            : '0 F'
                                        }
                                    </p>
                                </div>
                            </div>
                        </div>
                        
                        {/* Legend with amounts */}
                        <div className="grid grid-cols-2 gap-2 mt-3">
                            {(breakdown?.by_category || []).filter(c => c.value > 0).slice(0, 4).map((item, idx) => {
                                const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6'];
                                return (
                                    <div 
                                        key={idx} 
                                        className={`flex items-center gap-2 p-2 rounded-lg transition-all ${
                                            isDark ? 'hover:bg-gray-700/50' : 'hover:bg-gray-50'
                                        }`}
                                    >
                                        <div 
                                            className="w-3 h-3 rounded-full flex-shrink-0" 
                                            style={{ backgroundColor: colors[idx % colors.length] }}
                                        />
                                        <div className="min-w-0 flex-1">
                                            <p className={`text-xs font-medium truncate ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                                {item.name}
                                            </p>
                                            <p className={`text-[10px] ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                                {item.percentage}% • {item.formatted}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Top Products */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            Top 5 Produits
                        </h2>
                        <div className="space-y-3">
                            {topProducts.map((product) => (
                                <div key={product.rank} className={`flex items-center justify-between p-3 rounded-xl ${
                                    isDark ? 'bg-gray-700/50' : 'bg-gray-50'
                                }`}>
                                    <div className="flex items-center gap-3">
                                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                                            product.rank === 1 
                                                ? 'bg-yellow-500 text-white' 
                                                : product.rank === 2 
                                                    ? 'bg-gray-400 text-white'
                                                    : product.rank === 3 
                                                        ? 'bg-amber-700 text-white'
                                                        : isDark ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                                        }`}>
                                            {product.rank}
                                        </span>
                                        <div>
                                            <p className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                                {product.name}
                                            </p>
                                            <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                {product.quantity} vendus
                                            </p>
                                        </div>
                                    </div>
                                    <span className={`font-semibold text-sm ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                        {product.revenue_formatted}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ======================== OPERATIONS & SERVERS ======================== */}
                <div className="grid lg:grid-cols-2 gap-6">
                    {/* Operations */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                            isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                            <ChefHat className="w-5 h-5" />
                            Performance Opérationnelle
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <ChefHat className={`w-4 h-4 ${isDark ? 'text-orange-400' : 'text-orange-600'}`} />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Cuisine</span>
                                </div>
                                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {operations?.kitchen?.avg_time_formatted || '0 min'}
                                </p>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Wine className={`w-4 h-4 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Bar</span>
                                </div>
                                <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {operations?.bar?.avg_time_formatted || '0 min'}
                                </p>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <Clock className={`w-4 h-4 ${isDark ? 'text-amber-400' : 'text-amber-600'}`} />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>En retard</span>
                                </div>
                                <p className={`text-2xl font-bold ${
                                    (operations?.late_orders || 0) > 0 
                                        ? (isDark ? 'text-amber-400' : 'text-amber-600')
                                        : (isDark ? 'text-white' : 'text-gray-900')
                                }`}>
                                    {operations?.late_orders || 0}
                                </p>
                            </div>
                            <div className={`p-4 rounded-xl ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <div className="flex items-center gap-2 mb-2">
                                    <XCircle className={`w-4 h-4 ${isDark ? 'text-red-400' : 'text-red-600'}`} />
                                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Annulées</span>
                                </div>
                                <p className={`text-2xl font-bold ${
                                    (operations?.cancelled_orders || 0) > 0 
                                        ? (isDark ? 'text-red-400' : 'text-red-600')
                                        : (isDark ? 'text-white' : 'text-gray-900')
                                }`}>
                                    {operations?.cancelled_orders || 0}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Table Stats */}
                    <div className={`p-6 rounded-2xl ${
                        isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                    }`}>
                        <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${
                            isDark ? 'text-white' : 'text-gray-900'
                        }`}>
                            <Users className="w-5 h-5" />
                            Suivi des Tables
                        </h2>
                        <div className="grid grid-cols-3 gap-4">
                            <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <p className={`text-3xl font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>
                                    {tableStats?.open_tables || 0}
                                </p>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Ouvertes</p>
                            </div>
                            <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <p className={`text-3xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                    {tableStats?.tables_served_today || 0}
                                </p>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Servies</p>
                            </div>
                            <div className={`p-4 rounded-xl text-center ${isDark ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                                <p className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {tableStats?.avg_table_time_formatted || '0 min'}
                                </p>
                                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Temps moy.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ======================== SERVER PERFORMANCE TABLE ======================== */}
                <div className={`p-6 rounded-2xl ${
                    isDark ? 'bg-gray-800/60 border border-gray-700/50' : 'bg-white shadow-lg'
                }`}>
                    <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        Performance par Serveur / Caisse
                    </h2>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                                    <th className={`text-left py-3 px-4 text-sm font-semibold ${
                                        isDark ? 'text-gray-300' : 'text-gray-600'
                                    }`}>Utilisateur</th>
                                    <th className={`text-right py-3 px-4 text-sm font-semibold ${
                                        isDark ? 'text-gray-300' : 'text-gray-600'
                                    }`}>Commandes</th>
                                    <th className={`text-right py-3 px-4 text-sm font-semibold ${
                                        isDark ? 'text-gray-300' : 'text-gray-600'
                                    }`}>CA</th>
                                    <th className={`text-right py-3 px-4 text-sm font-semibold ${
                                        isDark ? 'text-gray-300' : 'text-gray-600'
                                    }`}>Panier moyen</th>
                                </tr>
                            </thead>
                            <tbody>
                                {byServer.map((server, idx) => (
                                    <tr 
                                        key={idx}
                                        className={`border-b transition-colors ${
                                            isDark 
                                                ? 'border-gray-700/50 hover:bg-gray-700/30' 
                                                : 'border-gray-100 hover:bg-gray-50'
                                        }`}
                                    >
                                        <td className={`py-3 px-4 font-medium ${
                                            isDark ? 'text-white' : 'text-gray-900'
                                        }`}>{server.name}</td>
                                        <td className={`py-3 px-4 text-right ${
                                            isDark ? 'text-gray-300' : 'text-gray-600'
                                        }`}>{server.orders}</td>
                                        <td className={`py-3 px-4 text-right font-semibold ${
                                            isDark ? 'text-emerald-400' : 'text-emerald-600'
                                        }`}>{server.revenue_formatted}</td>
                                        <td className={`py-3 px-4 text-right ${
                                            isDark ? 'text-gray-300' : 'text-gray-600'
                                        }`}>{server.average_formatted}</td>
                                    </tr>
                                ))}
                                {byServer.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className={`py-8 text-center ${
                                            isDark ? 'text-gray-400' : 'text-gray-500'
                                        }`}>
                                            Aucune donnée disponible
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </main>
        </div>
    );
};

export default PerformancePage;
